import { CodeLookup, ReferralOffer } from 'src/types/Affiliate'

// Affiliate referral capture. A visit to /r/:code or any URL with ?ref=:code stores the code in the
// `ss_ref` cookie (shared across *.swiftsole.app, 30 days) plus localStorage, and checkout sends it as
// `referralCode`. The last valid click wins: an unknown code never replaces a stored valid one.

const COOKIE_NAME = 'ss_ref'
const LEGACY_COOKIE_NAME = 'referralCode'
const STORAGE_KEY = 'ss_ref'
const MAX_AGE_SECONDS = 30 * 24 * 60 * 60
const CODE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/

/** Where /r/:code sends visitors when the lookup can't say (unknown code, malformed link, API unreachable). */
export const REFERRAL_FALLBACK_PATH = '/release/V2LIFETIME2022?code=INITIALOFF100'

export type StoredReferral = {
    code: string
    displayName?: string
    offer?: ReferralOffer // what a new customer gets through this code (no setup fee, maybe a trial)
    capturedAt: number
}

// Domain=.swiftsole.app is rejected on localhost and preview hosts, so only set it in production.
const cookieAttributes = () => {
    const { hostname, protocol } = window.location
    const domain = hostname === 'swiftsole.app' || hostname.endsWith('.swiftsole.app') ? '; Domain=.swiftsole.app' : ''
    const secure = protocol === 'https:' ? '; Secure' : ''
    return `; Path=/${domain}; SameSite=Lax${secure}`
}

const readCookie = (name: string) => {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : ''
}

const writeCookie = (code: string) => {
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(code)}; Max-Age=${MAX_AGE_SECONDS}${cookieAttributes()}`
}

const deleteCookie = () => {
    document.cookie = `${COOKIE_NAME}=; Max-Age=0${cookieAttributes()}`
}

const readStorage = (): StoredReferral | null => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed.code !== 'string') return null
        if (Date.now() - parsed.capturedAt > MAX_AGE_SECONDS * 1000) return null
        return parsed
    } catch {
        return null
    }
}

const writeStorage = (value: StoredReferral | null) => {
    try {
        if (value) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
        else window.localStorage.removeItem(STORAGE_KEY)
    } catch {
        // storage blocked (private mode); the cookie still carries the code
    }
}

/** The referral checkout should use, or null. The cookie is authoritative; localStorage is a fallback. */
export const getStoredReferral = (): StoredReferral | null => {
    const fromCookie = readCookie(COOKIE_NAME)
    const fromStorage = readStorage()

    if (fromCookie) {
        return fromStorage && fromStorage.code === fromCookie
            ? fromStorage
            : { code: fromCookie, capturedAt: Date.now() }
    }
    return fromStorage
}

/** After a successful checkout: forget the referral, including the pre-affiliate `referralCode` cookie. */
export const clearStoredReferral = () => {
    deleteCookie()
    writeStorage(null)
    document.cookie = `${LEGACY_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
}

const restore = (previous: StoredReferral | null) => {
    if (previous) {
        writeCookie(previous.code)
        writeStorage(previous)
    } else {
        deleteCookie()
        writeStorage(null)
    }
}

let pendingLookup: { code: string; promise: Promise<CodeLookup | null> } | null = null

const lookupCode = async (code: string): Promise<CodeLookup | null> => {
    const response = await fetch(`/api/v1/affiliates/code/${encodeURIComponent(code)}`, {
        credentials: 'include',
    })
    if (response.status === 404) return null
    if (!response.ok) throw new Error(`Referral lookup failed (${response.status})`)
    return response.json()
}

/**
 * Store `code` right away (so no auth redirect can lose it), then validate it in the background.
 * Resolves to the lookup, or null when the code is unknown. Rejects on network/server errors, in which
 * case the code stays stored and the backend validates it again at checkout.
 */
export const captureReferral = (code: string): Promise<CodeLookup | null> => {
    if (pendingLookup && pendingLookup.code === code) return pendingLookup.promise

    const previous = getStoredReferral()
    const written: StoredReferral = { code, capturedAt: Date.now() }
    writeCookie(code)
    writeStorage(written)

    const promise = lookupCode(code).then((result) => {
        const current = getStoredReferral()
        const stillOurs = current !== null && current.code === code

        if (!result) {
            // Unknown code: put back whatever valid code was stored before this visit.
            if (stillOurs) restore(previous && previous.code !== code ? previous : null)
            return null
        }

        if (stillOurs)
            writeStorage({ ...written, code: result.code, displayName: result.displayName, offer: result.offer })
        if (result.code !== code && stillOurs) writeCookie(result.code)
        return result
    })

    pendingLookup = { code, promise }
    // A failed lookup (network/5xx) is forgotten so the next capture of this code tries again.
    // This also avoids an unhandled rejection when nobody awaits the lookup.
    promise.catch(() => {
        if (pendingLookup && pendingLookup.promise === promise) pendingLookup = null
    })
    return promise
}

/** Resolves once any in-flight lookup has finished (successfully or not). */
export const referralSettled = (): Promise<void> =>
    pendingLookup ? pendingLookup.promise.then(() => undefined, () => undefined) : Promise.resolve()

/** A usable referral code from an already-decoded value, or null. */
export const validReferralCode = (candidate: string | null | undefined): string | null => {
    const code = candidate ? candidate.trim() : ''
    return CODE_PATTERN.test(code) ? code : null
}

const safeDecode = (value: string) => {
    try {
        return decodeURIComponent(value)
    } catch {
        // Malformed escape (e.g. /r/50%off): not a code we could have issued
        return null
    }
}

/** The referral code carried by a raw location (/r/:code or ?ref=), if any. */
export const referralCodeFromLocation = (pathname: string, search: string): string | null => {
    const pathMatch = pathname.match(/^\/r\/([^/]+)\/?$/)
    return validReferralCode(pathMatch ? safeDecode(pathMatch[1]) : new URLSearchParams(search).get('ref'))
}

/**
 * react-router's history (v4) throws on a path it can't decodeURI (e.g. a shared /r/50%off), which blanks the
 * whole app. Swap such a URL for a sane one before the router reads it.
 */
export const repairUndecodablePath = () => {
    try {
        decodeURI(window.location.pathname)
    } catch {
        const target = window.location.pathname.startsWith('/r/') ? REFERRAL_FALLBACK_PATH : '/'
        window.history.replaceState(null, '', target)
    }
}

/** Runs once before the app renders, so the code is stored before any login redirect can fire. */
export const captureReferralFromWindow = () => {
    const code = referralCodeFromLocation(window.location.pathname, window.location.search)
    if (code) captureReferral(code)
}
